<template>
  <div class="neo4j-reports-slot">
    <div
      v-if="!data.data.load"
      class="neo4j-reports-slot__query neo4j-reports-slot__query--hero"
    >
      <div v-if="!data.data.load" class="neo4j-reports-slot__intro">
        <h5 class="neo4j-reports-slot__heading mb-0">
          {{ $t("grid.widgets_section.neo_dash.ask.heading") }}
        </h5>
        <p class="neo4j-reports-slot__subtitle text-muted mb-0">
          {{ $t("grid.widgets_section.neo_dash.ask.subtitle") }}
        </p>
      </div>

      <div class="neo4j-reports-slot__searchbar">
        <b-form-input
          id="neo4j-ask-question"
          v-model="data.data.ask_question"
          class="neo4j-reports-slot__input"
          :placeholder="$t('grid.widgets_section.neo_dash.ask.placeholder')"
          autocomplete="off"
          @keyup.enter="generate"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="neo4j-reports-slot__generate text-capitalize"
          type="button"
          :disabled="!canGenerate"
          @click="generate"
        >
          {{ $t("grid.widgets_section.neo_dash.ask.generate") }}
        </b-button>
      </div>
    </div>

    <div v-if="data.data.load" class="neo4j-reports-slot__content">
      <div class="neo4j-reports-slot__toolbar">
        <button
          type="button"
          class="neo4j-reports-slot__toolbtn"
          :title="$t('grid.widgets_section.neo_dash.ask.toggle_query')"
          :aria-expanded="queryCollapsed ? 'false' : 'true'"
          @click="queryCollapsed = !queryCollapsed"
        >
          <span class="neo4j-reports-slot__toolbtn-ic">{{ queryCollapsed ? "🔍" : "▾" }}</span>
          <span class="neo4j-reports-slot__toolbtn-txt">
            {{ $t("grid.widgets_section.neo_dash.ask.toggle_query") }}
          </span>
        </button>

        <button
          v-if="!isDashboard"
          type="button"
          class="neo4j-reports-slot__toolbtn"
          :class="{ 'is-active': showParams }"
          :aria-expanded="showParams ? 'true' : 'false'"
          @click="showParams = !showParams"
        >
          <span class="neo4j-reports-slot__toolbtn-ic">⚙</span>
          <span class="neo4j-reports-slot__toolbtn-txt">
            {{
              showParams
                ? $t("grid.widgets_section.neo_dash.ask.hide_params")
                : $t("grid.widgets_section.neo_dash.ask.show_params")
            }}
          </span>
        </button>

        <button
          type="button"
          class="neo4j-reports-slot__toolbtn neo4j-reports-slot__toolbtn--new"
          @click="newQuery"
        >
          <span class="neo4j-reports-slot__toolbtn-ic">＋</span>
          <span class="neo4j-reports-slot__toolbtn-txt">
            {{ $t("grid.widgets_section.neo_dash.ask.new_query") }}
          </span>
        </button>
      </div>

      <div
        v-if="!queryCollapsed && !isDashboard"
        class="neo4j-reports-slot__requery"
      >
        <b-form-input
          v-model="data.data.ask_question"
          class="neo4j-reports-slot__input"
          :placeholder="$t('grid.widgets_section.neo_dash.ask.placeholder')"
          autocomplete="off"
          @keyup.enter="generate"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="neo4j-reports-slot__generate text-capitalize"
          type="button"
          :disabled="!canGenerate"
          @click="generate"
        >
          {{ $t("grid.widgets_section.neo_dash.ask.regenerate") }}
        </b-button>
      </div>

      <div v-if="showParams && !isDashboard" class="neo4j-reports-slot__params">
        <template v-if="isReport">
          <div class="neo4j-reports-slot__params-grid">
            <b-form-group
              v-if="paramHasDays"
              class="neo4j-reports-slot__params-field text-capitalize"
              :label="$t('grid.widgets_section.neo_dash.time_range')"
              label-for="neo4j-params-days"
            >
              <v-select
                id="neo4j-params-days"
                v-model="data.data.days"
                :options="daysOptions"
                :reduce="(opt) => opt.value"
                :clearable="false"
              />
            </b-form-group>

            <b-form-group
              v-if="paramHasColors"
              class="neo4j-reports-slot__params-field text-capitalize"
              :label="$t('grid.widgets_section.neo_dash.colors')"
              label-for="neo4j-params-colors"
            >
              <v-select
                id="neo4j-params-colors"
                v-model="data.data.colors"
                :options="colorOptions"
                :reduce="(opt) => opt.value"
                multiple
                :close-on-select="false"
                :placeholder="$t('grid.widgets_section.neo_dash.colors_placeholder')"
              />
            </b-form-group>

            <b-form-group
              v-if="paramHasPlate"
              class="neo4j-reports-slot__params-field text-capitalize"
              :label="$t('grid.widgets_section.neo_dash.report.plate')"
              label-for="neo4j-params-plate"
            >
              <b-form-input
                id="neo4j-params-plate"
                :value="data.data.plate"
                :placeholder="$t('grid.widgets_section.neo_dash.plate_route_placeholder')"
                @update="(v) => (data.data.plate = (v || '').toUpperCase())"
              />
            </b-form-group>

            <b-form-group
              v-if="paramHasMinSpeed"
              class="neo4j-reports-slot__params-field text-capitalize"
              :label="$t('grid.widgets_section.neo_dash.min_speed_label')"
              label-for="neo4j-params-min-speed"
            >
              <b-form-input
                id="neo4j-params-min-speed"
                type="number"
                min="0"
                step="5"
                :value="data.data.min_speed"
                :placeholder="$t('grid.widgets_section.neo_dash.min_speed_placeholder')"
                @update="(v) => (data.data.min_speed = v === '' ? 50 : Number(v))"
              />
            </b-form-group>

            <b-form-group
              v-if="paramHasWindow"
              class="neo4j-reports-slot__params-field text-capitalize"
              :label="$t('grid.widgets_section.neo_dash.window_seconds_label')"
              label-for="neo4j-params-window-seconds"
            >
              <b-form-input
                id="neo4j-params-window-seconds"
                type="number"
                min="1"
                max="300"
                step="1"
                :value="data.data.window_seconds"
                :placeholder="$t('grid.widgets_section.neo_dash.window_seconds_placeholder')"
                @update="(v) => (data.data.window_seconds = v === '' ? 10 : Number(v))"
              />
            </b-form-group>

            <b-form-group
              v-if="paramHasCameras"
              class="neo4j-reports-slot__params-field text-capitalize"
              :label="$t('grid.widgets_section.neo_dash.cameras')"
              label-for="neo4j-params-cameras"
            >
              <v-select
                id="neo4j-params-cameras"
                v-model="data.data.cameras"
                :options="cameraOptions"
                :reduce="(opt) => opt.value"
                multiple
                :close-on-select="false"
                :loading="camerasLoading"
                :placeholder="$t('grid.widgets_section.neo_dash.cameras_placeholder')"
              />
            </b-form-group>
          </div>

          <div class="neo4j-reports-slot__params-actions">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              size="sm"
              class="text-capitalize"
              type="button"
              @click="applyParams"
            >
              {{ $t("grid.widgets_section.neo_dash.ask.apply") }}
            </b-button>
          </div>
        </template>

        <!-- Genuine free-text (NL) query: no structured parameters to edit. -->
        <p v-else class="neo4j-reports-slot__params-note text-muted small mb-0">
          {{ $t("grid.widgets_section.neo_dash.ask.no_params") }}
        </p>
      </div>

      <div v-show="loading" class="neo4j-reports-slot__state">
        <div class="d-flex align-items-center">
          <b-spinner small />
          <span class="ml-1">{{ progressMessage || $t("grid.widgets_section.neo_dash.ask.processing") }}</span>
        </div>
        <p v-if="streamText" class="neo4j-reports-slot__stream text-muted small mb-0">{{ streamText }}</p>
      </div>
      <div v-show="!loading" class="neo4j-reports-slot__result">
        <lpr-analytics-dashboard v-if="isDashboard" :cameras="data.data.cameras" />
        <neo4j-report-view
          v-else
          ref="reportView"
          :key="refreshKey"
          :mode="data.data.mode === 'report' ? 'report' : 'ask'"
          :question="data.data.question || 'ask'"
          :ask-question="data.data.ask_question"
          :days="data.data.days"
          :colors="data.data.colors"
          :cameras="data.data.cameras"
          :plate="data.data.plate"
          :min-speed="data.data.min_speed"
          :window-seconds="data.data.window_seconds"
          @hook:mounted="syncLoading"
          @hook:updated="syncLoading"
        />
      </div>
    </div>

    <button
      type="button"
      class="neo4j-reports-slot__drawer-handle"
      :class="{ 'is-open': showExamples }"
      :aria-expanded="showExamples ? 'true' : 'false'"
      :title="$t('grid.widgets_section.neo_dash.ask.examples_title')"
      @click="toggleExamples"
    >
      <span class="neo4j-reports-slot__drawer-handle-ic">{{ showExamples ? "›" : "‹" }}</span>
      <span class="neo4j-reports-slot__drawer-handle-txt">
        {{ $t("grid.widgets_section.neo_dash.ask.examples_title") }}
      </span>
    </button>

    <transition name="neo4j-reports-slot__drawer-slide">
      <div v-if="showExamples" class="neo4j-reports-slot__drawer">
        <div class="neo4j-reports-slot__drawer-head">
          <span class="font-weight-bold">
            {{ $t("grid.widgets_section.neo_dash.ask.examples_title") }}
          </span>
          <button
            type="button"
            class="neo4j-reports-slot__drawer-close"
            aria-label="Close"
            @click="showExamples = false"
          >×</button>
        </div>
        <ul class="neo4j-reports-slot__drawer-list">
          <li v-for="q in exampleQuestions" :key="q.key">
            <button
              type="button"
              class="neo4j-reports-slot__example"
              @click="pickExample(q.key)"
            >
              {{ q.label }}
            </button>
          </li>
          <li>
            <button
              type="button"
              class="neo4j-reports-slot__example neo4j-reports-slot__example--dashboard"
              @click="openDashboard"
            >
              {{ $t("grid.widgets_section.neo_dash.dashboard.title") }}
            </button>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  BButton,
  BFormInput,
  BFormGroup,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import axios from "@axios";
import { REPORT_COLORS } from "./vehicleColors";

// Report question keys, mirror the API whitelist (graph-report endpoint).
const REPORT_QUESTIONS = [
  "cars_by_day",
  "cars_by_color",
  "cars_by_hour",
  "cars_by_camera",
  "vehicle_types",
  "vehicle_brands",
  "top_plates",
  "plates_registered",
  "camera_flow",
  "plate_clones",
  "plate_route",
  "over_speed",
  "exposure_time",
  "repeat_rate",
  "new_vs_repeat_hour",
  "convoy",
];

const NO_CAMERA_QUESTIONS = ["plates_registered", "cars_by_camera", "plate_route"];

export default {
  components: {
    BButton,
    BFormInput,
    BFormGroup,
    BSpinner,
    vSelect,
    LprAnalyticsDashboard: () => import("./LprAnalyticsDashboard.vue"),
    Neo4jReportView: () => import("./Neo4jReportView.vue"),
  },
  directives: {
    Ripple,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showExamples: false,
      queryCollapsed: false,
      showParams: false,
      loading: false,
      progressMessage: "",
      streamText: "",
      cameraOptions: [],
      camerasLoading: false,
      refreshKey: 0,
    };
  },
  computed: {
    exampleQuestions() {
      return REPORT_QUESTIONS.map((key) => ({
        key,
        label: this.$t(`grid.widgets_section.neo_dash.questions.${key}`),
      }));
    },
    canGenerate() {
      return !!(this.data.data.ask_question && this.data.data.ask_question.trim());
    },
    isDashboard() {
      return this.data.data.mode === "dashboard";
    },
    isReport() {
      return this.data.data.mode === "report";
    },
    resolvedReportKey() {
      const text = (this.data.data.ask_question || "").trim();
      if (!text) return null;
      const match = REPORT_QUESTIONS.find(
        (key) =>
          this.$t(`grid.widgets_section.neo_dash.questions.${key}`).trim() === text
      );
      return match || null;
    },
    // Options for the editable parameters panel (reused from the old form).
    colorOptions() {
      return REPORT_COLORS.map((key) => ({
        label: this.$t(`grid.widgets_section.neo_dash.color_names.${key}`),
        value: key,
      }));
    },
    daysOptions() {
      const lastDays = (n) => ({
        label: this.$t("grid.widgets_section.neo_dash.last_days", { n }),
        value: n,
      });
      return [
        lastDays(3),
        lastDays(7),
        lastDays(30),
        lastDays(90),
        { label: this.$t("grid.widgets_section.neo_dash.all_time"), value: 0 },
      ];
    },
    paramHasDays() {
      return this.data.data.question !== "plates_registered";
    },
    paramHasColors() {
      return this.data.data.question === "cars_by_color";
    },
    paramHasPlate() {
      return this.data.data.question === "plate_route";
    },
    paramHasMinSpeed() {
      return this.data.data.question === "over_speed";
    },
    paramHasWindow() {
      return this.data.data.question === "convoy";
    },
    paramHasCameras() {
      return !NO_CAMERA_QUESTIONS.includes(this.data.data.question);
    },
  },
  methods: {
    generate() {
      if (!this.canGenerate) return;
      const key = this.resolvedReportKey;
      if (key) {
        this.$set(this.data.data, "mode", "report");
        this.$set(this.data.data, "question", key);
      } else {
        // Free text (user typed or edited) -> NL graph-ask route.
        this.$set(this.data.data, "mode", "ask");
        this.$set(this.data.data, "question", "ask");
      }
      this.data.data.load = true;
      this.loading = true;
      this.refreshKey += 1;
    },
    syncLoading() {
      const view = this.$refs.reportView;
      this.loading = !!(view && view.loading);
      this.progressMessage = (view && view.progressMessage) || "";
      this.streamText = (view && view.streamText) || "";
    },
    pickExample(key) {
      this.$set(
        this.data.data,
        "ask_question",
        this.$t(`grid.widgets_section.neo_dash.questions.${key}`)
      );
      this.$set(this.data.data, "question", key);
      this.showExamples = false;
      this.generate();
    },
    openDashboard() {
      this.$set(this.data.data, "mode", "dashboard");
      this.data.data.load = true;
      this.loading = false;
      this.showExamples = false;
      this.showParams = false;
      this.refreshKey += 1;
    },
    newQuery() {
      this.$set(this.data.data, "ask_question", "");
      this.$set(this.data.data, "question", "ask");
      this.$set(this.data.data, "mode", "ask");
      this.data.data.load = false;
      this.loading = false;
      this.showParams = false;
      this.queryCollapsed = false;
    },
    applyParams() {
      if (!this.isReport) return;
      this.loading = true;
      this.refreshKey += 1;
    },
    toggleExamples() {
      this.showExamples = !this.showExamples;
    },
    loadCameras() {
      this.camerasLoading = true;
      axios
        .get("/v1/detection_event/graph-cameras")
        .then(({ data }) => {
          const list = (data && data.data) || [];
          this.cameraOptions = list.map((c) => ({
            label: c.name ? c.name : `Cámara ${c.id}`,
            value: String(c.id),
          }));
        })
        .catch(() => {
          this.cameraOptions = [];
        })
        .finally(() => {
          this.camerasLoading = false;
        });
    },
    refreshPage() {
      this.refreshKey += 1;
    },
  },
  mounted() {
    if (typeof this.data.data.ask_question !== "string") {
      this.$set(this.data.data, "ask_question", "");
    }
    if (!Array.isArray(this.data.data.cameras)) {
      this.$set(this.data.data, "cameras", []);
    }
    if (!Array.isArray(this.data.data.colors) || !this.data.data.colors.length) {
      this.$set(this.data.data, "colors", ["red", "green"]);
    }
    if (typeof this.data.data.days !== "number") {
      this.$set(this.data.data, "days", 7);
    }
    if (typeof this.data.data.plate !== "string") {
      this.$set(this.data.data, "plate", "");
    }
    if (typeof this.data.data.min_speed !== "number") {
      this.$set(this.data.data, "min_speed", 50);
    }
    if (typeof this.data.data.window_seconds !== "number") {
      this.$set(this.data.data, "window_seconds", 10);
    }

    ["baseUrl", "database", "reportType", "query", "neo4jProtocol", "neo4jUser",
      "neo4jPassword", "neo4jHost", "neo4jPort"].forEach((key) => {
      if (key in this.data.data) this.$delete(this.data.data, key);
    });

    if (!["ask", "report", "dashboard"].includes(this.data.data.mode)) {
      this.$set(this.data.data, "mode", "ask");
    }

    this.loadCameras();
  },
};
</script>

<style lang="scss" scoped>
.neo4j-reports-slot {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.neo4j-reports-slot__query {
  flex: 0 0 auto;
  padding: 8px 10px;

  &--hero {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
    padding: 16px;
  }
}

.neo4j-reports-slot__intro {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.neo4j-reports-slot__heading {
  font-weight: 600;
}

.neo4j-reports-slot__subtitle {
  font-size: 0.85rem;
}

.neo4j-reports-slot__searchbar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  .neo4j-reports-slot__query--hero & {
    max-width: 480px;
  }
}

.neo4j-reports-slot__input {
  flex: 1 1 auto;
  border-radius: 999px;
}

.neo4j-reports-slot__generate {
  flex: 0 0 auto;
  border-radius: 999px;
  white-space: nowrap;
}

.neo4j-reports-slot__content {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  z-index: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  /* Keep a slim gutter on the right so the centered example tab never sits
     on top of the chart/graph content. */
  padding-right: 30px;
}

.neo4j-reports-slot__result {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > * {
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* Persistent toolbar: always visible while a result is shown so a new query
   can always be launched. Leaves right room for the example tab. */
.neo4j-reports-slot__toolbar {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 6px;
}

.neo4j-reports-slot__toolbtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(130, 134, 139, 0.3);
  padding: 2px 8px;
  background: rgba(130, 134, 139, 0.12);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.78rem;
  line-height: 1.3;
  color: inherit;

  &:hover {
    background: rgba(130, 134, 139, 0.26);
  }

  &.is-active {
    background: #4a6b8a;
    border-color: #4a6b8a;
    color: #fff;
  }

  &--new {
    margin-left: auto;
  }
}

.neo4j-reports-slot__toolbtn-ic {
  font-size: 0.9rem;
  line-height: 1;
}

/* Compact editable query + re-generate shown over the result. */
.neo4j-reports-slot__requery {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 10px 6px 6px;
}

/* Editable parameters panel for the active predefined query. */
.neo4j-reports-slot__params {
  flex: 0 0 auto;
  padding: 6px 10px 8px 6px;
  border: 1px solid rgba(130, 134, 139, 0.25);
  border-radius: 8px;
  margin: 0 6px 6px 6px;
  background: rgba(130, 134, 139, 0.06);
}

.neo4j-reports-slot__params-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 6px 12px;
}

.neo4j-reports-slot__params-field {
  margin-bottom: 0;

  ::v-deep label {
    margin-bottom: 2px;
    font-size: 0.75rem;
  }
}

.neo4j-reports-slot__params-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.neo4j-reports-slot__params-note {
  margin: 0;
}

.neo4j-reports-slot__stream {
  max-width: 640px;
  margin-top: 0.5rem;
  text-align: center;
}

.neo4j-reports-slot__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  height: 100%;
}

.neo4j-reports-slot__drawer-handle {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  z-index: 7;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(74, 107, 138, 0.35);
  border-right: none;
  padding: 12px 7px;
  background: #4a6b8a;
  color: #fff;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: 0.02em;
  box-shadow: -2px 2px 10px rgba(0, 0, 0, 0.16);
  transition: right 0.2s ease, background 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #5f88ad;
    box-shadow: -3px 2px 12px rgba(0, 0, 0, 0.22);
  }

  &.is-open {
    right: 260px;
  }
}

.neo4j-reports-slot__drawer-handle-ic {
  font-size: 1.05rem;
  line-height: 1;
}

/* Stack the label vertically along the tab so it stays slim and never
   intrudes into the chart/graph area. */
.neo4j-reports-slot__drawer-handle-txt {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  white-space: nowrap;
}

/* Very short widgets: drop the label so the tab stays compact. */
@media (max-height: 240px) {
  .neo4j-reports-slot__drawer-handle-txt {
    display: none;
  }
}

.neo4j-reports-slot__drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 260px;
  max-width: 90%;
  z-index: 6;
  display: flex;
  flex-direction: column;
  background: var(--white, #fff);
  border-left: 1px solid rgba(130, 134, 139, 0.3);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.neo4j-reports-slot__drawer-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(130, 134, 139, 0.25);
}

.neo4j-reports-slot__drawer-close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  color: inherit;
}

.neo4j-reports-slot__drawer-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 6px;
}

.neo4j-reports-slot__example {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  line-height: 1.25;
  color: inherit;

  &:hover {
    background: rgba(130, 134, 139, 0.15);
  }

  &--dashboard {
    margin-top: 6px;
    border-top: 1px solid rgba(130, 134, 139, 0.25);
    font-style: italic;
  }
}

.neo4j-reports-slot__drawer-slide-enter-active,
.neo4j-reports-slot__drawer-slide-leave-active {
  transition: transform 0.2s ease;
}

.neo4j-reports-slot__drawer-slide-enter,
.neo4j-reports-slot__drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>
